import React, { useState } from 'react';
import { Building, Shield, Settings as SettingsIcon, RotateCcw, Users, CreditCard, Database, Calendar, Bell, Building2 } from 'lucide-react';

import Organization from './settings/Organization';
import Security from './settings/Security';
import Config from './settings/Config';
import DateTimeSettings from './settings/DateTimeSettings';
import StripeIntegration from './settings/StripeIntegration';
import Reset from './settings/Reset';
import RbacManager from './settings/RbacManager';
import DatabaseManagement from './settings/DatabaseManagement';
import NotificationSettings from './settings/NotificationSettings';
import CompanyMaster from './settings/CompanyMaster';
import PermissionGuard from './PermissionGuard';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('organization');
  const [user, setUser] = useState(null);

  React.useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const tabs = [
    { id: 'companies', label: 'Company Master', icon: Building2, masterOnly: true, adminOnly: true },
    { id: 'organization', label: 'Organization', icon: Building },
    { id: 'datetime', label: 'Date & Time', icon: Calendar },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'config', label: 'Config', icon: SettingsIcon },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'rbac', label: 'Roles & Permissions', icon: Users, adminOnly: true },
    { id: 'database', label: 'Database Management', icon: Database, permission: 'settings.database.manage' },
    { id: 'stripe', label: 'Stripe Integration', icon: CreditCard },
    { id: 'reset', label: 'Reset', icon: RotateCcw }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'companies': return <CompanyMaster />;
      case 'organization': return <Organization />;
      case 'datetime': return <DateTimeSettings />;
      case 'security': return <Security />;
      case 'config': return <Config />;
      case 'notifications': return <NotificationSettings />;
      case 'rbac': return <RbacManager />;
      case 'database': return <DatabaseManagement />;
      case 'stripe': return <StripeIntegration />;
      case 'reset': return <Reset />;
      default: return <Organization />;
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-gray-50">
      {/* Sidebar - horizontal scroll on mobile, vertical on desktop */}
      <div className="md:w-64 bg-white shadow-sm border-b md:border-b-0 md:border-r flex-shrink-0">
        <div className="p-4 md:p-6 flex md:block items-center justify-between">
          <h1 className="text-lg md:text-xl font-semibold text-gray-900">Settings</h1>
        </div>
        <nav className="px-3 pb-3 md:pb-0 flex md:block overflow-x-auto md:overflow-x-visible gap-1 md:gap-0">
          {tabs.map((tab) => {
            // Hide admin-only tabs for non-admin users
            if (tab.adminOnly && user?.role !== 'ADMIN') {
              return null;
            }
            // Hide masterOnly tabs for non-master tenants
            if (tab.masterOnly && user?.tenantCode) {
              return null;
            }
            
            const Icon = tab.icon;
            const TabButton = (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 mb-0 md:mb-1 text-left rounded-md transition-colors whitespace-nowrap text-sm ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700 border-b-2 md:border-b-0 md:border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 mr-2 md:mr-3 flex-shrink-0" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
            
            return tab.permission ? (
              <PermissionGuard key={tab.id} permission={tab.permission} showFallback={false}>
                {TabButton}
              </PermissionGuard>
            ) : TabButton;
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 md:p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;